// Display figures from exact integers and decimal strings. No float touches an amount or a
// price: digits move as strings, and integer arithmetic is BigInt.

export const COLLATERAL = 18;
export const PAPER = 18;
export const USDC = 6;
export const MINUS = "−";

export const big = (raw: string | null | undefined): bigint => {
  if (!raw) return 0n;
  try {
    return BigInt(raw);
  } catch {
    return 0n;
  }
};

/** A raw integer at `decimals` as its exact decimal string, trailing zeros dropped. */
export function fromRaw(raw: string | bigint, decimals: number): string {
  let text = typeof raw === "bigint" ? raw.toString() : raw.trim();
  const negative = text.startsWith("-");
  if (negative) text = text.slice(1);
  text = text.replace(/^0+/, "") || "0";
  if (decimals === 0) return (negative && text !== "0" ? "-" : "") + text;
  const padded = text.padStart(decimals + 1, "0");
  const whole = padded.slice(0, padded.length - decimals);
  const fraction = padded.slice(padded.length - decimals).replace(/0+$/, "");
  const zero = /^0*$/.test(whole) && fraction === "";
  return `${negative && !zero ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

/** A decimal string as its raw integer at `decimals`, toward zero; null if it is not one. */
export function toRaw(text: string, decimals: number): bigint | null {
  const clean = text.trim().replace(/,/g, "");
  const match = /^(-?)(\d*)(?:\.(\d*))?$/.exec(clean);
  if (!match || (!match[2] && !match[3])) return null;
  const whole = match[2] || "0";
  const fraction = (match[3] ?? "").slice(0, decimals).padEnd(decimals, "0");
  const value = BigInt(whole + fraction);
  return match[1] ? -value : value;
}

/** Rounds a decimal string to `places`, half away from zero. */
export function roundDec(dec: string, places: number): string {
  const negative = dec.startsWith("-");
  const body = negative ? dec.slice(1) : dec;
  const [whole = "0", fraction = ""] = body.split(".");
  if (fraction.length <= places) return dec;
  const kept = BigInt((whole || "0") + fraction.slice(0, places));
  const next = Number(fraction[places] ?? "0");
  const rounded = next >= 5 ? kept + 1n : kept;
  const text = fromRaw(rounded, places);
  const zero = /^0(\.0*)?$/.test(text);
  return negative && !zero ? `-${text}` : text;
}

function group(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

const UNITS = [
  { at: 12, suffix: "T" },
  { at: 9, suffix: "B" },
  { at: 6, suffix: "M" },
  { at: 3, suffix: "K" },
];

export type Format = {
  /** Places kept (rounded half away from zero). */
  places?: number;
  /** Places always shown, zero padded. Defaults to `places`. */
  min?: number;
  /** `+` before a positive figure. */
  sign?: boolean;
  /** Abbreviate at or above this many whole digits (`4` turns 1234 into 1.23K). */
  compact?: number;
  prefix?: string;
  suffix?: string;
};

/** A decimal string for the screen: rounded, grouped, signed, abbreviated. */
export function fmt(dec: string, options: Format = {}): string {
  const { places = 2, sign = false, prefix = "", suffix = "" } = options;
  let min = options.min ?? places;
  let negative = dec.startsWith("-");
  let body = negative ? dec.slice(1) : dec;
  let unit = "";
  if (options.compact !== undefined) {
    const digits = (body.split(".")[0] ?? "").replace(/^0+/, "").length;
    if (digits >= options.compact) {
      const found = UNITS.find((u) => digits > u.at);
      if (found) {
        body = shift(body, found.at);
        unit = found.suffix;
        min = Math.min(min, 2);
      }
    }
  }
  let rounded = roundDec(body, unit ? Math.min(places, 2) : places);
  const [whole = "0", fraction = ""] = rounded.split(".");
  const shown = fraction.padEnd(min, "0");
  rounded = `${group(whole)}${shown ? `.${shown}` : ""}`;
  if (/^[0,.]*$/.test(rounded)) negative = false;
  const lead = negative ? MINUS : sign && !/^[0,.]*$/.test(rounded) ? "+" : "";
  return `${lead}${prefix}${rounded}${unit}${suffix}`;
}

/** Moves the decimal point `places` to the left. */
function shift(dec: string, places: number): string {
  const [whole = "0", fraction = ""] = dec.split(".");
  const padded = whole.padStart(places + 1, "0");
  const head = padded.slice(0, padded.length - places);
  return `${head}.${padded.slice(padded.length - places)}${fraction}`;
}

/** Collateral (18 decimals) in dollars. */
export const usd = (raw: string | bigint, options: Format = {}) => fmt(fromRaw(raw, COLLATERAL), { prefix: "$", ...options });

/** Dollars with a sign, for PnL. */
export const pnl = (raw: string | bigint, options: Format = {}) =>
  fmt(fromRaw(raw, COLLATERAL), { prefix: "$", sign: true, ...options });

/** USDC's own 6 decimals in dollars. */
export const usdc = (raw: string | bigint, options: Format = {}) => fmt(fromRaw(raw, USDC), { prefix: "$", ...options });

/** PAPER (18 decimals). */
export const paper = (raw: string | bigint, options: Format = {}) => fmt(fromRaw(raw, PAPER), { places: 2, ...options });

/** Places a price scale stands for: 10 is one, 100 two. */
export const scalePlaces = (scale: string) => Math.max(0, scale.length - 1);

/** A raw price at `scale` as its decimal, grouped, at the scale's places. */
export const price = (raw: string | bigint, scale: string, options: Format = {}) => {
  const places = scalePlaces(scale);
  return fmt(fromRaw(raw, places), { places, ...options });
};

/** Hundredths of a percent as a percentage. */
export const pctBp = (bp: string | bigint | number, options: Format = {}) =>
  fmt(fromRaw(BigInt(bp), 2), { places: 2, suffix: "%", ...options });

/** Millionths as a percentage. */
export const pctPpm = (ppm: string | bigint | number, options: Format = {}) =>
  fmt(fromRaw(BigInt(ppm), 4), { places: 2, suffix: "%", ...options });

/** `a / b` in hundredths of a percent, toward zero; 0 for a zero `b`. */
export const ratioBp = (a: bigint, b: bigint): bigint => (b === 0n ? 0n : (a * 10_000n) / b);

/** The change from `from` to `to` (decimal strings) in hundredths of a percent. */
export function changeBp(from: string, to: string): bigint | null {
  const a = toRaw(from, 8);
  const b = toRaw(to, 8);
  if (a === null || b === null || a === 0n) return null;
  return ((b - a) * 10_000n) / a;
}

export const sign = (raw: string | bigint): -1 | 0 | 1 => {
  const value = typeof raw === "bigint" ? raw : big(raw);
  return value > 0n ? 1 : value < 0n ? -1 : 0;
};

/** A tone class for a signed figure. */
export const tone = (raw: string | bigint): "up" | "down" | "flat" => {
  const s = sign(raw);
  return s > 0 ? "up" : s < 0 ? "down" : "flat";
};

export const min = (a: bigint, b: bigint) => (a < b ? a : b);
export const max = (a: bigint, b: bigint) => (a > b ? a : b);
